// ============================================================================
// NewTradeModal — shared, page-agnostic trade form.
//
// Lives at Layout level so any page (Dashboard / Signals / Favorites /
// Trading) can open it via `useOutletContext().openTrade(prefill)`. The
// previous flow forced a navigation to /dashboard/trading just so the
// inline form in TradingPage could render — that jerked the user away
// from wherever they were and made the modal feel like a route change.
//
// Self-contained:
//   • Owns its own form state. Caller only passes a `prefill` payload.
//   • Fetches the portfolio on open (cash + heat) so it can size + warn.
//   • Calls apiExecuteTrade, toasts the result, then closes.
//
// Responsiveness:
//   • Mobile (≤768px): inherits `.modal-content` from index.css which
//     anchors the card to the bottom of the viewport as a sheet.
//   • Desktop: centered card, capped at 92vh height, inner-scrollable
//     so long forms (option leg + risk projection) never push the
//     CTA off-screen on small laptops.
// ============================================================================

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DollarSign, X, AlertTriangle } from 'lucide-react';
import Input from './ui/Input';
import Button from './ui/Button';
import { useToast } from './ui/Toast';
import { apiExecuteTrade, apiGetPortfolio, apiGetPortfolioHeat } from '../utils/backendApi';
import { computeSuggestedPositionSize, RISK_PER_TRADE_PCT, getQuote } from '../utils/marketEngine';
import { useExchange } from '../context/ExchangeContext';

const emptyPrefill = {
  ticker: '', direction: 'BUY', type: 'stock',
  price: '', quantity: '',
  optionType: 'CALL', optionStrike: '', optionExpiry: '', optionContractSymbol: '',
  notes: '', signalSource: '',
  stopPrice: null, targetPrice: null, swingDays: null,
};

export default function NewTradeModal({ prefill, onClose, onSuccess }) {
  const toast = useToast();
  const { config } = useExchange();
  const cs = config?.currencySymbol || '$';

  // Form state — initialized from prefill on mount + every time prefill changes.
  const [formTicker,    setFormTicker]    = useState('');
  const [formDir,       setFormDir]       = useState('BUY');
  const [formType,      setFormType]      = useState('stock');
  const [formQty,       setFormQty]       = useState('');
  const [formPrice,     setFormPrice]     = useState('');
  const [formOptType,   setFormOptType]   = useState('CALL');
  const [formOptStrike, setFormOptStrike] = useState('');
  const [formOptExpiry, setFormOptExpiry] = useState('');
  const [formOptContractSymbol, setFormOptContractSymbol] = useState('');
  const [formSignalSource,      setFormSignalSource]      = useState('');
  const [formNotes,     setFormNotes]     = useState('');
  const [formStop,      setFormStop]      = useState('');
  const [formTarget,    setFormTarget]    = useState('');
  const [formSwing,     setFormSwing]     = useState('');

  // Portfolio data fetched on open so we can size + warn without depending on
  // whichever page opened us.
  const [portfolio, setPortfolio] = useState(null);
  const [heat,      setHeat]      = useState(null);
  const [executing, setExecuting] = useState(false);
  // Guards the one-shot auto-suggest: we fill the qty field ONCE after the
  // portfolio loads, then never again. Without this, clearing the last digit
  // re-triggers the suggest effect (blank qty → refill) so the user can't
  // empty the field to type their own number. Reset to false on every new
  // prefill so a freshly-opened modal gets its suggestion.
  const didSuggestRef = useRef(false);

  // Initialize / re-initialize form whenever prefill changes (i.e. when a
  // different signal opens the modal). Refs would also work but using state
  // and re-setting on prefill change keeps the React data flow clean.
  useEffect(() => {
    didSuggestRef.current = false;   // allow one auto-suggest for this open
    const p = { ...emptyPrefill, ...(prefill || {}) };
    setFormTicker(String(p.ticker || '').toUpperCase());
    setFormPrice(p.price != null ? String(p.price?.toFixed?.(2) ?? p.price) : '');
    setFormType(p.type || 'stock');
    setFormNotes(p.notes || '');
    setFormStop(p.stopPrice != null ? String(p.stopPrice) : '');
    setFormTarget(p.targetPrice != null ? String(p.targetPrice) : '');
    setFormSwing(p.swingDays != null ? String(p.swingDays) : '');
    setFormSignalSource(p.signalSource || '');
    if (p.type === 'option') {
      const optType = p.optionType || 'CALL';
      setFormOptType(optType);
      setFormDir(optType === 'CALL' ? 'BUY' : 'SHORT');
      setFormOptStrike(p.optionStrike != null ? String(p.optionStrike) : '');
      setFormOptExpiry(p.optionExpiry || '');
      setFormOptContractSymbol(p.optionContractSymbol || '');
    } else {
      setFormDir(p.direction || 'BUY');
    }
    setFormQty('');   // intentionally blank — we set it after the portfolio loads
  }, [prefill]);

  // Load portfolio + heat once per open so the sizing hint + risk projection
  // have data. Failures degrade gracefully — the form still executes; the
  // hints just don't show.
  useEffect(() => {
    let cancelled = false;
    apiGetPortfolio().then(p => { if (!cancelled) setPortfolio(p?.portfolio || null); }).catch(() => {});
    apiGetPortfolioHeat().then(h => { if (!cancelled) setHeat(h || null); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // One-shot auto-suggest: once the portfolio loads, fill the qty field with
  // the Kelly-sized suggestion — but ONLY the first time per modal-open.
  // After that the field belongs to the user, so deleting the last digit
  // leaves it blank instead of snapping back to the suggestion. The user can
  // still re-apply the suggestion via the "Suggested" link in the cost
  // summary. `formQty` is deliberately NOT a dependency — that's exactly the
  // loop we're avoiding.
  useEffect(() => {
    if (didSuggestRef.current) return;
    const cash = parseFloat(portfolio?.cash_balance);
    if (!Number.isFinite(cash) || cash <= 0) return;
    const entry = parseFloat(formPrice);
    if (!Number.isFinite(entry) || entry <= 0) return;
    const suggestion = computeSuggestedPositionSize({
      type:         formType,
      currentPrice: entry,
      stop:         Number.isFinite(parseFloat(formStop)) ? parseFloat(formStop) : null,
      option:       formType === 'option' ? { premium: entry } : null,
      // Pass through the Kelly inputs from the originating signal so the
      // sizer can scale by edge instead of using the flat 0.75% fallback.
      confidence:   prefill?.confidence,
      riskReward:   prefill?.riskReward,
    }, cash);
    if (suggestion?.qty) {
      setFormQty(String(suggestion.qty));
      didSuggestRef.current = true;   // never auto-fill again this open
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio, formPrice, formStop, formType, prefill]);

  // Esc closes the modal — standard modal behaviour, mirrors SecurityDetailModal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cashBalance = parseFloat(portfolio?.cash_balance) || 0;

  // Per-trade risk projection — same math as TradingPage's inline form.
  const formRiskProjection = useMemo(() => {
    const qty   = parseFloat(formQty);
    const entry = parseFloat(formPrice);
    if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(entry) || entry <= 0) return null;
    const cash = parseFloat(portfolio?.cash_balance);
    if (!Number.isFinite(cash) || cash <= 0) return null;

    let tradeRisk;
    if (formType === 'option') {
      tradeRisk = qty * entry * 100 * 0.5;
    } else {
      const stop = parseFloat(formStop);
      if (Number.isFinite(stop) && stop > 0) tradeRisk = qty * Math.abs(entry - stop);
      else tradeRisk = qty * entry * 0.02;
    }
    const equity       = cash + (heat?.positionsValue || 0);
    const currentHeat  = heat?.totalHeatDollars || 0;
    const projHeatPct  = equity > 0 ? ((currentHeat + tradeRisk) / equity) * 100 : 0;
    const tradeRiskPct = equity > 0 ? (tradeRisk / equity) * 100 : 0;

    const suggestion = computeSuggestedPositionSize({
      type:         formType,
      currentPrice: entry,
      stop:         Number.isFinite(parseFloat(formStop)) ? parseFloat(formStop) : null,
      option:       formType === 'option' ? { premium: entry } : null,
      confidence:   prefill?.confidence,
      riskReward:   prefill?.riskReward,
    }, cash);

    const heatLimit = heat?.thresholds?.heatWarnPct ?? 6;
    return {
      tradeRisk:        +tradeRisk.toFixed(2),
      tradeRiskPct:     +tradeRiskPct.toFixed(2),
      projHeatPct:      +projHeatPct.toFixed(2),
      heatLimit,
      overHeat:         projHeatPct > heatLimit,
      overTradeRisk:    tradeRiskPct > 1.5,
      suggestedQty:     suggestion?.qty ?? null,
      sizingMode:       suggestion?.sizingMode ?? null,
      sizingRiskPct:    suggestion?.equityRiskPct ?? null,
    };
  }, [formQty, formPrice, formStop, formType, portfolio, heat, prefill]);

  const executeTrade = useCallback(async () => {
    if (!formTicker || !formQty || !formPrice) { toast.error('Fill all required fields'); return; }
    setExecuting(true);
    try {
      const result = await apiExecuteTrade({
        ticker: formTicker.toUpperCase(), direction: formDir,
        assetType: formType, quantity: parseFloat(formQty), price: parseFloat(formPrice),
        optionType:           formType === 'option' ? formOptType   : null,
        optionStrike:         formType === 'option' ? parseFloat(formOptStrike) : null,
        optionExpiry:         formType === 'option' ? formOptExpiry : null,
        optionContractSymbol: formType === 'option' ? (formOptContractSymbol || null) : null,
        signalSource: formSignalSource || 'Manual',
        notes: formNotes,
        stopPrice:   formStop   ? parseFloat(formStop)   : null,
        targetPrice: formTarget ? parseFloat(formTarget) : null,
        swingDays:   formSwing  ? parseInt(formSwing, 10) : null,
      });
      toast.success(result.message);
      onSuccess?.(result);
      onClose?.();
    } catch (e) {
      toast.error(e?.message || 'Trade failed');
    }
    setExecuting(false);
  }, [
    formTicker, formDir, formType, formQty, formPrice,
    formOptType, formOptStrike, formOptExpiry, formOptContractSymbol,
    formSignalSource, formNotes, formStop, formTarget, formSwing,
    toast, onSuccess, onClose,
  ]);

  const totalCost    = parseFloat(formQty || 0) * parseFloat(formPrice || 0) * (formType === 'option' ? 100 : 1);
  const tradeVariant = formType === 'option'
    ? (formOptType === 'CALL' ? 'long' : 'short')
    : (formDir === 'BUY' ? 'long' : 'short');
  const tradeLabel   = formType === 'option'
    ? `Buy ${formOptType} — ${cs}${totalCost.toFixed(2)}`
    : `${formDir === 'BUY' ? '↑ Execute Buy' : '↓ Execute Short'} — ${cs}${totalCost.toFixed(2)}`;

  return (
    <div
      className="animate-fade-in"
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        background: 'var(--bg-overlay)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        className="animate-slide-up glass modal-content"
        style={{
          width: '100%', maxWidth: 520,
          maxHeight: '92vh', overflow: 'auto',
          borderRadius: 'var(--r-xl)',
          border: '1px solid rgba(91,106,240,0.20)',
          padding: '24px 24px 20px',
          boxShadow: 'var(--shadow-modal)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 18, gap: 12,
          position: 'sticky', top: 0, zIndex: 1,
          background: 'var(--bg-card)',
          paddingBottom: 4,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, borderRadius: 'var(--r-md)',
              background: 'linear-gradient(135deg, rgba(91,106,240,0.20), rgba(91,106,240,0.06))',
              border: '1px solid rgba(91,106,240,0.30)',
            }}>
              <DollarSign size={16} color="var(--accent-soft)" strokeWidth={2.4} />
            </span>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.2px' }}>Execute Trade</h3>
          </div>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Close trade form" />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Ticker + Direction */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input
              label="Ticker"
              value={formTicker}
              onChange={e => setFormTicker(e.target.value.toUpperCase())}
              placeholder="AAPL"
              size="md"
            />
            {formType === 'option' ? (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Action</div>
                <div style={{
                  padding: '10px 14px', borderRadius: 'var(--r-md)', fontSize: 13, fontWeight: 700,
                  background: formOptType === 'CALL' ? 'var(--green-dim)' : 'var(--purple-dim)',
                  color:      formOptType === 'CALL' ? 'var(--green)'    : '#c4b5fd',
                  border: `1px solid ${formOptType === 'CALL' ? 'rgba(34,197,94,0.30)' : 'rgba(168,85,247,0.30)'}`,
                  textAlign: 'center',
                }}>
                  Buy {formOptType}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Direction</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['BUY', 'SHORT'].map(d => {
                    const active = formDir === d;
                    return (
                      <button key={d} type="button" onClick={() => setFormDir(d)} style={{
                        flex: 1, padding: '9px 0', borderRadius: 'var(--r-md)', cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                        background: active ? (d === 'BUY' ? 'var(--grad-success)' : 'var(--grad-danger)') : 'transparent',
                        color:      active ? '#fff' : 'var(--text-secondary)',
                        border: active ? 'none' : '1px solid var(--border)',
                        boxShadow: active ? (d === 'BUY' ? '0 4px 12px rgba(34,197,94,0.25)' : '0 4px 12px rgba(239,68,68,0.25)') : 'none',
                      }}>{d === 'BUY' ? '↑ BUY' : '↓ SHORT'}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Qty + Price */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input
              label={formType === 'option' ? 'Contracts' : 'Shares'}
              value={formQty}
              onChange={e => setFormQty(e.target.value)}
              placeholder="1"
              type="number"
              size="md"
            />
            <Input
              label={formType === 'option' ? 'Premium / share ($)' : 'Price ($)'}
              value={formPrice}
              onChange={e => setFormPrice(e.target.value)}
              placeholder="5.00"
              type="number"
              step="0.01"
              size="md"
              onFocus={async () => { if (formTicker && !formPrice) { try { const q = await getQuote(formTicker); setFormPrice(q.price.toFixed(2)); } catch {} } }}
            />
          </div>

          {/* Take-profit + stop-loss */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input
              label="Take profit ($)"
              value={formTarget}
              onChange={e => setFormTarget(e.target.value)}
              placeholder="—"
              type="number"
              step="0.01"
              size="md"
              hint={formTarget ? 'Auto-closes when price hits this level' : 'Leave blank to disable'}
            />
            <Input
              label="Stop loss ($)"
              value={formStop}
              onChange={e => setFormStop(e.target.value)}
              placeholder="—"
              type="number"
              step="0.01"
              size="md"
              hint={formStop ? 'Auto-closes when price hits this level' : 'Leave blank to disable'}
            />
          </div>

          {/* Asset Type */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Asset Type</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['stock', 'option'].map(t => {
                const active = formType === t;
                return (
                  <button key={t} type="button" onClick={() => setFormType(t)} style={{
                    flex: 1, padding: '9px 0', borderRadius: 'var(--r-md)', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700, fontFamily: 'inherit', textTransform: 'capitalize',
                    background: active ? 'var(--grad-accent)' : 'transparent',
                    color:      active ? '#fff' : 'var(--text-secondary)',
                    border: active ? 'none' : '1px solid var(--border)',
                    boxShadow: active ? '0 4px 12px rgba(91,106,240,0.25)' : 'none',
                  }}>{t}</button>
                );
              })}
            </div>
          </div>

          {/* Option fields */}
          {formType === 'option' && (
            <div className="animate-fade-in" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Call / Put</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['CALL', 'PUT'].map(t => {
                    const active = formOptType === t;
                    return (
                      <button
                        key={t} type="button"
                        onClick={() => { setFormOptType(t); setFormDir(t === 'CALL' ? 'BUY' : 'SHORT'); }}
                        style={{
                          flex: 1, padding: '8px 0', borderRadius: 'var(--r-sm)', cursor: 'pointer',
                          fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
                          background: active ? (t === 'CALL' ? 'var(--grad-success)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)') : 'transparent',
                          color:      active ? '#fff' : 'var(--text-secondary)',
                          border: active ? 'none' : '1px solid var(--border)',
                        }}>{t}</button>
                    );
                  })}
                </div>
              </div>
              <Input label="Strike" value={formOptStrike} onChange={e => setFormOptStrike(e.target.value)} type="number" step="0.5" size="md" />
              <Input label="Expiry" value={formOptExpiry} onChange={e => setFormOptExpiry(e.target.value)} type="date" size="md" />
            </div>
          )}

          {/* Cost summary */}
          <div style={{
            background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)',
            padding: '12px 14px', border: '1px solid var(--border-subtle)',
          }}>
            {formType === 'option' && (
              <div style={{ fontSize: 10, color: '#c4b5fd', marginBottom: 8 }}>
                {formQty || 0} contract{formQty !== '1' ? 's' : ''} × ${formPrice || 0} premium × 100 shares/contract
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Total Cost</span>
              <span className="mono" style={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px' }}>
                {cs}{totalCost.toFixed(2)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
              <span style={{ color: 'var(--text-muted)' }}>Cash Available</span>
              <span className="mono" style={{ color: '#38bdf8', fontWeight: 600 }}>{cs}{cashBalance.toFixed(2)}</span>
            </div>
            {formRiskProjection && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Risk if stopped out</span>
                  <span className="mono" style={{ color: formRiskProjection.overTradeRisk ? '#f59e0b' : 'var(--text-primary)', fontWeight: 600 }}>
                    {cs}{formRiskProjection.tradeRisk.toFixed(2)} ({formRiskProjection.tradeRiskPct.toFixed(2)}% of equity)
                  </span>
                </div>
                {formRiskProjection.suggestedQty != null && formRiskProjection.suggestedQty !== parseFloat(formQty) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                    <span
                      style={{ color: 'var(--text-muted)', cursor: 'help' }}
                      title={formRiskProjection.sizingMode === 'kelly'
                        ? `Half-Kelly: edge-proportional sizing from signal win-rate × reward-to-risk. Caps at ${(0.025 * 100).toFixed(1)}% of equity for safety.`
                        : 'Fixed-fraction fallback used when the signal has no validated win rate yet.'}
                    >
                      Suggested ({((formRiskProjection.sizingRiskPct ?? RISK_PER_TRADE_PCT) * 100).toFixed(2)}% risk · {formRiskProjection.sizingMode === 'kelly' ? 'half-Kelly' : 'fixed'})
                    </span>
                    <button
                      onClick={() => setFormQty(String(formRiskProjection.suggestedQty))}
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        background: 'none', border: 0, padding: 0, cursor: 'pointer',
                        color: 'var(--accent-soft)', fontWeight: 600, textDecoration: 'underline dotted',
                      }}
                      title={`Click to size at ${formRiskProjection.suggestedQty}`}
                    >
                      {formRiskProjection.suggestedQty} {formType === 'option' ? 'contracts' : 'shares'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Risk guard advisory — non-blocking */}
          {formRiskProjection && (formRiskProjection.overHeat || formRiskProjection.overTradeRisk) && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '8px 12px', borderRadius: 'var(--r-md)',
              background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)',
            }}>
              <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 11, color: '#fbbf24', lineHeight: 1.5 }}>
                {formRiskProjection.overTradeRisk && (
                  <div>
                    Single-trade risk is <strong>{formRiskProjection.tradeRiskPct.toFixed(2)}%</strong> of equity — above the {(RISK_PER_TRADE_PCT * 100).toFixed(2)}% target.
                  </div>
                )}
                <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Advisory only — your trade will still execute if you proceed.</div>
              </div>
            </div>
          )}

          <Button
            variant={tradeVariant}
            size="lg"
            fullWidth
            loading={executing}
            onClick={executeTrade}
          >
            {tradeLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
